import { useEffect, useState } from "react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { GlassPanel } from "@/components/ui/glass-panel";
import { AnimatedTabs } from "@/components/ui/animated-tabs";
import { Badge } from "@/components/ui/badge";
import { useLocation } from "@/components/location-provider";
import { useUnit } from "@/components/unit-provider";
import { PageHeader } from "@/components/page-header";
import { Thermometer, Droplets, Wind, Sun, Sunrise, Sunset } from "lucide-react";
import { wmoInfo } from "@/lib/weather-codes";

interface LongRangeDay {
  date: string;
  weatherCode: number;
  tMaxC: number | null;
  tMinC: number | null;
  popPct: number | null;
  precipMm: number | null;
  snowCm: number | null;
  windMaxMs: number | null;
  gustMaxMs: number | null;
  uvMax: number | null;
}

export default function ForecastPage() {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [longRange, setLongRange] = useState<LongRangeDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("temperature");
  const { current: location } = useLocation();
  const { display } = useUnit();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`/api/weather?lat=${location.lat}&lon=${location.lon}`).then((r) => r.json()),
      fetch(`/api/forecast?lat=${location.lat}&lon=${location.lon}`)
        .then((r) => r.json())
        .catch(() => ({ days: [] })),
    ])
      .then(([w, f]) => {
        setData(w.weather);
        setLongRange((f.days || []) as LongRangeDay[]);
      })
      .finally(() => setLoading(false));
  }, [location.lat, location.lon]);

  if (loading) return (
    <div className="space-y-5 animate-slide-up">
      <Skeleton className="h-80 rounded-2xl" />
      <Skeleton className="h-72 rounded-2xl" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-44 rounded-2xl" />)}
      </div>
    </div>
  );

  if (!data) return (
    <div className="flex flex-col items-center justify-center py-20 text-center animate-slide-up">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted/50 mb-4">
        <Thermometer className="h-8 w-8 text-muted-foreground/40" />
      </div>
      <h2 className="text-lg font-semibold">Unable to load forecast</h2>
      <p className="text-sm text-muted-foreground mt-1">Check your connection and try again</p>
    </div>
  );

  const daily = (data.daily as { dt: number; temp: { min: number; max: number; day: number }; pop: number; humidity: number; wind_speed: number; sunrise: number; sunset: number; weather: { description: string; icon: string }[]; uvi?: number }[]) || [];
  const hourly = (data.hourly as { dt: number; temp: number; pop: number; humidity: number }[])?.slice(0, 48) || [];

  const tempData = daily.slice(0, 8).map((d) => ({
    day: new Date(d.dt * 1000).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }),
    high: Math.round(d.temp.max),
    low: Math.round(d.temp.min),
  }));

  const precipData = hourly.map((h) => ({
    time: new Date(h.dt * 1000).toLocaleTimeString([], { hour: "numeric" }),
    rain: Math.round(h.pop * 100),
  }));

  const humidityData = hourly.map((h) => ({
    time: new Date(h.dt * 1000).toLocaleTimeString([], { hour: "numeric" }),
    humidity: h.humidity,
  }));

  return (
    <div className="space-y-5 animate-slide-up">
      <PageHeader
        eyebrow="Extended"
        title="Forecast"
        subtitle="Temperature trend, precipitation probability, and daily detail"
      />

      <GlassPanel intensity="low" className="p-5">
        <div className="flex items-center justify-between mb-4">
          <AnimatedTabs
            tabs={[
              { id: "temperature", label: "Temperature" },
              { id: "precipitation", label: "Precipitation" },
              { id: "humidity", label: "Humidity" },
            ]}
            active={view}
            onChange={setView}
          />
          <span className="text-[10px] text-muted-foreground/50 hidden sm:block">{daily.length} days · {location.name}</span>
        </div>

        <div className="h-64">
          {view === "temperature" && (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={tempData} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="highGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="oklch(0.7 0.18 40)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="oklch(0.7 0.18 40)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="lowGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="oklch(0.65 0.15 220)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="oklch(0.65 0.15 220)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} unit="°" />
                <Tooltip
                  contentStyle={{ borderRadius: 12, fontSize: 12, border: "1px solid var(--border)", background: "var(--popover)", color: "var(--popover-foreground)", boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}
                  labelStyle={{ color: "var(--foreground)", fontWeight: 600 }}
                  cursor={{ stroke: "var(--border)", strokeWidth: 1 }}
                />
                <Area type="monotone" dataKey="high" stroke="oklch(0.7 0.18 40)" fill="url(#highGrad)" strokeWidth={2} name="High" unit="°C" />
                <Area type="monotone" dataKey="low" stroke="oklch(0.65 0.15 220)" fill="url(#lowGrad)" strokeWidth={2} name="Low" unit="°C" />
              </AreaChart>
            </ResponsiveContainer>
          )}
          {view === "precipitation" && (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={precipData} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                <XAxis dataKey="time" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} interval={5} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} unit="%" />
                <Tooltip
                  contentStyle={{ borderRadius: 12, fontSize: 12, border: "1px solid var(--border)", background: "var(--popover)", color: "var(--popover-foreground)", boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}
                  labelStyle={{ color: "var(--foreground)", fontWeight: 600 }}
                  cursor={{ fill: "var(--muted)", opacity: 0.3 }}
                />
                <Bar dataKey="rain" fill="oklch(0.65 0.15 220)" radius={[4, 4, 0, 0]} name="Rain %" />
              </BarChart>
            </ResponsiveContainer>
          )}
          {view === "humidity" && (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={humidityData} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="humidGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="oklch(0.65 0.18 145)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="oklch(0.65 0.18 145)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="time" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} interval={5} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} unit="%" domain={[0, 100]} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, fontSize: 12, border: "1px solid var(--border)", background: "var(--popover)", color: "var(--popover-foreground)", boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}
                  labelStyle={{ color: "var(--foreground)", fontWeight: 600 }}
                  cursor={{ stroke: "var(--border)", strokeWidth: 1 }}
                />
                <Area type="monotone" dataKey="humidity" stroke="oklch(0.65 0.18 145)" fill="url(#humidGrad)" strokeWidth={2} name="Humidity" unit="%" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </GlassPanel>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {daily.slice(0, 8).map((d) => (
          <GlassPanel key={d.dt} intensity="low" hover className="p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-muted-foreground">
                {new Date(d.dt * 1000).toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" })}
              </p>
              {d.pop > 0.1 && (
                <Badge variant="info" size="sm">{Math.round(d.pop * 100)}% rain</Badge>
              )}
            </div>
            <div className="flex items-center gap-3">
              <img src={`/api/icon/${d.weather?.[0]?.icon}@2x.png`} alt="" className="h-12 w-12 drop-shadow-lg" />
              <div>
                <p className="text-xl font-bold tracking-tight">{Math.round(d.temp.max)}°</p>
                <p className="text-xs text-muted-foreground">Lo {Math.round(d.temp.min)}°</p>
              </div>
            </div>
            <p className="text-xs capitalize text-muted-foreground mt-2">{d.weather?.[0]?.description}</p>
            <div className="mt-3 pt-3 border-t border-border/20 grid grid-cols-2 gap-2 text-[10px]">
              <div className="flex items-center gap-1 text-muted-foreground">
                <Droplets className="h-2.5 w-2.5" />
                <span>{d.humidity}%</span>
              </div>
              <div className="flex items-center gap-1 text-muted-foreground">
                <Wind className="h-2.5 w-2.5" />
                <span>{d.wind_speed.toFixed(1)} m/s</span>
              </div>
              <div className="flex items-center gap-1 text-muted-foreground">
                <Sunrise className="h-2.5 w-2.5" />
                <span>{new Date(d.sunrise * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
              </div>
              <div className="flex items-center gap-1 text-muted-foreground">
                <Sunset className="h-2.5 w-2.5" />
                <span>{new Date(d.sunset * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
              </div>
            </div>
          </GlassPanel>
        ))}
      </div>

      {longRange.length > 0 && (
        <GlassPanel intensity="low" className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">16-Day Outlook</h3>
              <p className="text-[10px] text-muted-foreground/60 mt-0.5">Open-Meteo · extended range</p>
            </div>
            <Badge variant="outline" size="sm">{longRange.length} days</Badge>
          </div>
          <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
            {longRange.map((d) => {
              const info = wmoInfo(d.weatherCode);
              const Icon = info.icon;
              const dayDate = new Date(d.date + "T00:00:00");
              return (
                <div
                  key={d.date}
                  className="rounded-xl border border-border/30 bg-background/40 p-3 flex flex-col items-center text-center gap-1.5"
                >
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {dayDate.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}
                  </p>
                  <Icon className={`h-6 w-6 ${info.color}`} />
                  <p className="text-lg font-bold tracking-tight leading-none">
                    {d.tMaxC != null ? `${Math.round(d.tMaxC)}°` : "—"}
                    <span className="text-xs font-medium text-muted-foreground ml-1">
                      {d.tMinC != null ? `${Math.round(d.tMinC)}°` : ""}
                    </span>
                  </p>
                  <p className="text-[10px] leading-tight text-muted-foreground line-clamp-2 min-h-[24px]">{info.label}</p>
                  <div className="w-full pt-1.5 mt-auto border-t border-border/20 flex items-center justify-between text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Droplets className="h-2.5 w-2.5" />
                      {d.popPct != null ? `${d.popPct}%` : "—"}
                    </span>
                    <span className="flex items-center gap-1">
                      <Wind className="h-2.5 w-2.5" />
                      {d.windMaxMs != null ? `${Math.round(d.windMaxMs)}` : "—"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </GlassPanel>
      )}
    </div>
  );
}